import {
  collectNestedCtaFields,
  type CtaFieldSink,
} from "@/lib/contentParsers/contentItemCtaFields";
import { isConditionalCtaContentItem } from "@/lib/contentParsers/contentItemTypeGuards";
import type {
  ContentItem,
  TextContent,
} from "@/lib/contentParsers/contentItemSchemas";
import {
  ContentSectionType,
  CTA_TYPE_BUTTON,
  CTA_TYPE_BUTTON_OVERLAY,
  CTA_TYPE_CONDITIONAL_CTA,
} from "@/types/contentSectionType";

const isTextContent = (x: unknown): x is TextContent => {
  if (typeof x !== "object" || x === null || !("type" in x)) return false;
  const t = (x as { type: ContentSectionType }).type;
  return (
    t === ContentSectionType.Text &&
    typeof (x as { text?: string }).text === "string"
  );
};

const pushText = (out: string[], s: string | undefined) => {
  if (s != null && s.length > 0) out.push(s);
};

const collectFromNested = (out: string[], node: unknown): void => {
  if (node == null) return;
  if (isTextContent(node)) {
    pushText(out, node.text);
    return;
  }
  if (Array.isArray(node)) {
    for (const el of node) collectFromNested(out, el);
    return;
  }
  if (typeof node === "object") {
    for (const v of Object.values(node as Record<string, unknown>)) {
      collectFromNested(out, v);
    }
  }
};

export const collectWysiwygTextsFromContent = (
  content: ContentItem[],
): string[] => {
  const out: string[] = [];
  for (const item of content) {
    switch (item.type) {
      case ContentSectionType.Text:
        pushText(out, item.text);
        break;
      case CTA_TYPE_BUTTON:
      case CTA_TYPE_BUTTON_OVERLAY:
        pushText(out, item.text);
        break;
      case CTA_TYPE_CONDITIONAL_CTA:
        if (isConditionalCtaContentItem(item)) {
          const nestedSink: CtaFieldSink = {
            ctaKeys: [],
            ctaTexts: [],
            redirectTos: [],
            redirectServiceSnippets: [],
          };
          for (const opt of item.options) {
            nestedSink.ctaKeys.length = 0;
            nestedSink.ctaTexts.length = 0;
            nestedSink.redirectTos.length = 0;
            nestedSink.redirectServiceSnippets.length = 0;
            collectNestedCtaFields(nestedSink, opt.cta);
            for (const t of nestedSink.ctaTexts) {
              pushText(out, t);
            }
          }
        }
        break;
      case ContentSectionType.Grid:
        for (const cell of item.cells ?? []) {
          for (const cc of cell.content) collectFromNested(out, cc);
        }
        if (item.cell != null) {
          for (const cc of item.cell.content) collectFromNested(out, cc);
        }
        break;
      case ContentSectionType.MonthOverviewDetails:
        collectFromNested(out, item.paragraph1);
        collectFromNested(out, item.paragraph2);
        collectFromNested(out, item.paragraph3);
        break;
      case ContentSectionType.MonthlyOverview:
        collectFromNested(out, item.moneyIn);
        collectFromNested(out, item.moneyOut);
        collectFromNested(out, item.creditCard);
        collectFromNested(out, item.savings);
        break;
      default:
        break;
    }
  }
  return out;
};

export const collectWysiwygTextsFromAdvisor = (
  advisorSummaryText: string | undefined,
): string[] => {
  const out: string[] = [];
  pushText(out, advisorSummaryText);
  return out;
};
